import { useContext } from "react";
import { Field as BaseField } from "rc-field-form";
import { useTranslation } from "react-i18next";
import Field from "../../../../../components/Field";
import BaseSettingsFormContent from "../../../../../components/Form/BaseSettingsFormContent";
import Text from "../../../../../components/Inputs/Text";
import Search from "../../../../../components/Map/Search";
import ResourceSelect from "../../../../../components/Inputs/ResourceSelect";
import {
	renderOrgLevelResourceLabel,
	getSettingsOrgResourceSearchFilters,
	renderSettingsResourceLabel,
	getSettingsResourceSearchFilters,
} from "../../../../../utils/helpers/settings";
import { OrgLevelGroupsContext } from "../../OrganisationLevelGroups/context";
import { FormGroup } from "reactstrap";
import { Wrapper } from "@googlemaps/react-wrapper";
import Select from "../../../../../components/Inputs/Select";
import { getCompaniesSearchFilters } from "../../../../Profile/Personal/Actions/ChangeCompanyInformation";
import Map from "../../OrganisationLevels/Form/Map/index";
import { getUserName } from "../../../ClockIn/Flyers/Form";
import { getUserSearchFilters } from "../../../../Profile/Personal/Actions/ChangeManagement";

function Content({
	mode,
	marker,
	markerError,
	center,
	disabled,
	isMasterCompany,
	onManageAbilityChange,
	search,
	setSearch,
	handleSelect,
	coords,
	setCoordinates,
	zoom,
	setZoom,
	setCenter,
}) {
	const { t } = useTranslation();
	const { groups } = useContext(OrgLevelGroupsContext);

	return (
		<>
			<BaseSettingsFormContent
				mode={mode}
				disabled={disabled}
			/>

			<Field name="glSegment" label={t("gl-segment")}>
				<Text disabled={disabled} placeholder={t("gl-segment")} />
			</Field>

			<Field
				name="timezone"
				label={t("timezone")}
				rules={[
					{
						required: true,
						message: t("required-timezone"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="text"
					resourcePath="/timezone?pagination=false"
					hasCompany={false}
					hasSearch
					placeholder={t("timezone")}
					disabled={disabled}
				/>
			</Field>

			<h3 className="font-weight-300">{t("manage-ability")}</h3>

			<Field
				name="manageAbility"
				label={t("show-on")}
				rules={[
					{
						required: true,
						message: t("required-show-on")
					}
				]}
			>
				<Select
					placeholder={t("show-on")}
					onChange={onManageAbilityChange}
					disabled={disabled}
				>
					<Select.Option value="all" key="all">
						{t("all")}
					</Select.Option>
					<Select.Option value="custom" key="custom">
						{t("custom")}
					</Select.Option>
					<Select.Option value="by-company" key="by-company">
						{t("by-company")}
					</Select.Option>
					<Select.Option value="by-pay-groups" key="by-pay-groups">
						{t("by-pay-groups")}
					</Select.Option>
					<Select.Option value="by-unions" key="by-unions">
						{t("by-unions")}
					</Select.Option>
					<Select.Option value="by-users" key="by-users">
						{t("by-users")}
					</Select.Option>
					<Select.Option value="by-groupings" key="by-groupings">
						{t("by-groupings")}
					</Select.Option>
					<Select.Option value="by-user-groupings" key="by-user-groupings">
						{t("by-user-groupings")}
					</Select.Option>
				</Select>
			</Field>

			<BaseField shouldUpdate>
				{({ }, { }, { getFieldValue }) => {
					const manageAbility = getFieldValue("manageAbility");
					const isCustom = manageAbility === "custom";

					const manageByPayGroups = ["custom", "by-pay-groups"].includes(manageAbility);
					const manageByCompany = ["custom", "by-company"].includes(manageAbility);
					const manageByUnions = ["custom", "by-unions"].includes(manageAbility);
					const manageByUsers = ["custom", "by-users"].includes(manageAbility);
					const manageByGroupings = ["custom", "by-groupings"].includes(manageAbility);
					const manageByUserGroupings = ["custom", "by-user-groupings"].includes(manageAbility);

					return (
						<>
							{manageByCompany && (
								<Field
									name="companies"
									label={t("companies")}
									rules={
										!isCustom && [
											{
												required: true,
												message: t("required-companies"),
											},
										]
									}
									dependencies={["manageAbility"]}
								>
									<ResourceSelect
										labelPropName="name"
										resourcePath="/companies"
										placeholder={t("select-companies")}
										hasSearch
										getSearchFilters={getCompaniesSearchFilters}
										mode="multiple"
										disabled={disabled}
									/>
								</Field>
							)}

							{manageByPayGroups && (
								<Field
									name="payGroups"
									label={t("pay-groups")}
									rules={
										!isCustom && [
											{
												required: true,
												message: t(
													"required-pay-group",
												),
											},
										]
									}
									dependencies={["manageAbility"]}
								>
									<ResourceSelect
										mode="multiple"
										labelPropName="description"
										resourcePath="/pay-group"
										placeholder={t("pay-groups")}
										renderLabel={
											renderSettingsResourceLabel
										}
										hasSearch
										getSearchFilters={
											getSettingsResourceSearchFilters
										}
										disabled={disabled}
									/>
								</Field>
							)}

							{manageByUnions && (
								<Field
									name="unions"
									label={t("unions")}
									rules={
										!isCustom && [
											{
												required: true,
												message: t("required-unions"),
											},
										]
									}
									dependencies={["manageAbility"]}
								>
									<ResourceSelect
										labelPropName="description"
										resourcePath="/union"
										placeholder={t("unions")}
										renderLabel={renderSettingsResourceLabel}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										mode="multiple"
										disabled={disabled}
									/>
								</Field>
							)}

							{manageByUsers && (
								<Field
									name="users"
									label={t("users")}
									rules={
										!isCustom && [
											{
												required: true,
												message: t("required-users"),
											},
										]
									}
									dependencies={["manageAbility"]}
								>
									<ResourceSelect
										labelPropName="name"
										resourcePath="/users"
										placeholder={t("users")}
										renderLabel={getUserName}
										hasSearch
										getSearchFilters={getUserSearchFilters}
										mode="multiple"
										disabled={disabled}
									/>
								</Field>
							)}

							{manageByGroupings && (
								<Field
									name="groupings"
									label={t("groupings")}
									rules={
										!isCustom && [
											{
												required: true,
												message: t("required-groupings"),
											},
										]
									}
								>
									<ResourceSelect
										labelPropName="description"
										resourcePath="/grouping"
										placeholder={t("user-groups")}
										renderLabel={renderSettingsResourceLabel}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										mode="multiple"
										disabled={disabled}
									/>
								</Field>
							)}

							{manageByUserGroupings && (
								<Field
									name="userGroupings"
									label={t("user-groups")}
									rules={
										!isCustom && [
											{
												required: true,
												message: t("required-user-groups"),
											},
										]
									}
								>
									<ResourceSelect
										labelPropName="description"
										resourcePath="/user-group?pagination=false"
										placeholder={t("user-groups")}
										renderLabel={renderSettingsResourceLabel}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										mode="multiple"
										disabled={disabled}
									/>
								</Field>
							)}
						</>
					)
				}}
			</BaseField>

			{groups.map((group) => (
				<Field
					name={`${group.level}`.replace("_", "")}
					label={renderOrgLevelResourceLabel(group)}
				>
					<ResourceSelect
						labelPropName="description"
						resourcePath={`/${group.id}/org-levels`}
						placeholder={
							renderOrgLevelResourceLabel(group)
						}
						renderLabel={renderOrgLevelResourceLabel}
						allowClear
						hasSearch
						getSearchFilters={getSettingsOrgResourceSearchFilters}
						mode="multiple"
						disabled={disabled}
					/>
				</Field>
			))}

			<h3 className="font-weight-300">{t("address-details")}</h3>

			<FormGroup>
				<Wrapper apiKey={`${process.env.REACT_APP_GOOGLE_MAP_KEY}&libraries=places,drawing`}>
					<Search
						search={search}
						setSearch={setSearch}
						handleSelect={handleSelect}
					/>
				</Wrapper>
			</FormGroup>

			<Field
				name="country"
				rules={[{ required: true, message: t("required-country") }]}
			>
				<ResourceSelect
					labelPropName="name"
					resourcePath="/countries?pagination=false"
					hasCompany={false}
					placeholder={t("country")}
					hasSearch
					disabled={disabled}
				/>
			</Field>

			<Field
				name="state"
				rules={[
					{
						required: true,
						message: t("required-state"),
					},
				]}
			>
				<Text placeholder={t("state")} disabled={disabled} />
			</Field>

			<Field
				name="city"
				rules={[
					{
						required: true,
						message: t("required-city"),
					},
				]}
			>
				<Text placeholder={t("city")} disabled={disabled} />
			</Field>

			<Field name="address1">
				<Text placeholder={t("address1")} disabled={disabled} />
			</Field>

			<Field name="address2">
				<Text placeholder={t("address2")} disabled={disabled} />
			</Field>

			<Field
				name="postalCode"
				rules={[
					{
						requried: true,
						message: t("required-postal-code"),
					},
				]}
			>
				<Text placeholder={t("postal-code")} disabled={disabled} />
			</Field>
			
			<div style={{ height: 550 }} className="mb-2">
				<Map
					coordinates={coords}
					setCoordinates={setCoordinates}
					center={center}
					zoom={zoom}
					marker={marker}
					setCenter={setCenter}
					setZoom={setZoom}
				/>
			</div>
			{markerError && <div className="text-danger my-2">{t("marker-required")}</div>}
		</>
	);
}

export default Content;
